import React, { useEffect, useState } from "react";
import IntlCurrencyInput from "react-intl-currency-input";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import driverService from "../../../service/driver.service";

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

function formatCPF(cpf) {
  if (!cpf) return "";
  const digits = String(cpf).replace(/\D/g, "");
  if (digits.length !== 11) return String(cpf);
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function isSeparatorOrPlaceholder(driver) {
  if (!driver || typeof driver !== "object") return true;
  if (driver.__separator === true) return true;
  const name = driver.name || "";
  if (name === "Não aplicar motorista") return true;
  if (name.includes("---")) return true;
  return false;
}

function getDriverLabel(option, formatName) {
  if (!option || typeof option !== "object") return "";
  if (option.__separator === true) return "";
  return formatName(option.name || "");
}

function isSameDriver(option, value) {
  if (!option || !value) return false;
  if (typeof option !== "object" || typeof value !== "object") return false;

  const optionKey = option.id || option.uid || option.value || option.name;
  const valueKey = value.id || value.uid || value.value || value.name;

  return optionKey === valueKey;
}

function resolveDriverDocId(driverChose, uidDriver) {
  if (driverChose && typeof driverChose === "object") {
    const id = driverChose.id || driverChose.uid;
    if (id) return id;
  }
  if (uidDriver) return uidDriver;
  return "";
}

function buildPlacasLine(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return "";
  const parts = [];
  if (vehicle.vehiclePlate) parts.push(`Cavalo: ${vehicle.vehiclePlate}`);
  if (vehicle.bodyworkPlate) parts.push(`Carroceria: ${vehicle.bodyworkPlate}`);
  if (Array.isArray(vehicle.reboques) && vehicle.reboques.length > 0) {
    const plates = vehicle.reboques.map((r) => r.placaReboque).filter(Boolean);
    if (plates.length) parts.push(`Reboque(s): ${plates.join(", ")}`);
  }
  return parts.join(" | ");
}

function buildVehicleDescription(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return "";
  return [vehicle.vehicleType, vehicle.bodyworkType, vehicle.descricaoVeiculo]
    .filter(Boolean)
    .join(" · ");
}

function ReadonlyField({ label, value, loading }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        component="span"
        sx={{
          display: "block",
          mb: 0.5,
          lineHeight: 1.35,
          maxWidth: "100%",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          hyphens: "auto",
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={20} sx={{ mt: 0.5 }} />
      ) : (
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          value={value || "—"}
          InputProps={{ readOnly: true }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-input": {
              backgroundColor: "#f5f5f5",
              cursor: "default",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            },
          }}
        />
      )}
    </Box>
  );
}

function FreightDriverNegotiationCard({
  view,
  user,
  canViewFreightDriver,
  canViewFreightDriverNegotiation,
  verifyPayment,
  valueDriverInitial,
  valueDriverFinal,
  valueNegotiated,
  paymentCondition,
  driverChose,
  driversInFila,
  uidDriver,
  formatName,
  onDriverInitialChange,
  onDriverFinalChange,
  onNegotiatedChange,
  onPaymentConditionChange,
  onDriverSelect,
}) {
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [complementary, setComplementary] = useState({
    cpf: "",
    plates: "",
    vehicle: "",
  });

  useEffect(() => {
    let cancelled = false;

    async function loadComplementary() {
      const docId = resolveDriverDocId(driverChose, uidDriver);
      const skip =
        !docId ||
        (driverChose &&
          typeof driverChose === "object" &&
          isSeparatorOrPlaceholder(driverChose));

      if (skip) {
        setComplementary({ cpf: "", plates: "", vehicle: "" });
        setLoadingDetail(false);
        return;
      }

      setLoadingDetail(true);
      try {
        const snap = await driverService.getDriverAvailableById(docId);
        if (cancelled) return;

        if (!snap.exists) {
          const cpfFallback =
            driverChose && typeof driverChose === "object" && driverChose.cpf
              ? driverChose.cpf
              : "";
          setComplementary({
            cpf: cpfFallback ? formatCPF(cpfFallback) : "",
            plates: "",
            vehicle: "",
          });
          setLoadingDetail(false);
          return;
        }

        const allData = snap.data() || {};
        const personal = allData.personalData || {};
        const vehicle = allData.vehicle || {};

        const cpfRaw =
          personal.documentCpf ||
          (driverChose && typeof driverChose === "object" && driverChose.cpf) ||
          "";

        setComplementary({
          cpf: cpfRaw ? formatCPF(cpfRaw) : "",
          plates: buildPlacasLine(vehicle),
          vehicle: buildVehicleDescription(vehicle),
        });
      } catch (e) {
        if (!cancelled) {
          console.error("FreightDriverNegotiationCard: erro ao carregar motorista", e);
          setComplementary({ cpf: "", plates: "", vehicle: "" });
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    }

    loadComplementary();
    return () => {
      cancelled = true;
    };
  }, [driverChose, uidDriver]);

  const cpfFromChose =
    driverChose &&
    typeof driverChose === "object" &&
    driverChose.cpf &&
    !isSeparatorOrPlaceholder(driverChose)
      ? formatCPF(driverChose.cpf)
      : "";

  const displayCpf = complementary.cpf || cpfFromChose || "";

  const selectedDriver =
    driverChose && typeof driverChose === "object" && !isSeparatorOrPlaceholder(driverChose)
      ? driverChose
      : null;
  const driverOptions = [
    {
      name: "Não aplicar motorista",
      cpf: "",
      value: "",
      id: "",
      uid: "",
    },
    ...driversInFila,
  ];

  const hiddenFieldStyle = user?.permissoes?.freightDriver?.isView ? undefined : { display: "none" };

  return (
    <Card
      sx={{
        padding: 2,
        paddingLeft: 0,
        width: "100%",
        display: "block",
        boxSizing: "border-box",
      }}
      className="mb-3 form-signin mx-auto mb-4 mb-lg-6 border border-[10px] border-[#344d66] rounded-lg p-3 "
      style={{
        background: "#ffffff",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
        borderRadius: "16px",
      }}
    >
      <h4 className="mb-3">
        <DriveEtaIcon sx={{ verticalAlign: "middle", mr: 0.25 }} />
        {user?.permissoes?.freightDriver?.isView && (
          <HandshakeOutlinedIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />
        )}
        {user?.permissoes?.freightDriver?.isView ? "Motorista e Negociação" : "Motorista"}
      </h4>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={4} lg={3}>
          <label htmlFor="nameDriver" className="font-test">
            Motorista
          </label>
          <Autocomplete
            disabled={view || !user?.permissoes?.freightViewEditDriver?.isView}
            id="chose-1"
            size="small"
            value={selectedDriver}
            getOptionLabel={(option) => getDriverLabel(option, formatName)}
            options={driverOptions}
            isOptionEqualToValue={isSameDriver}
            getOptionDisabled={(option) => option.__separator === true}
            onChange={(event, value) => {
              onDriverSelect(isSeparatorOrPlaceholder(value) ? null : value);
            }}
            clearOnBlur
            noOptionsText="Nenhum Motorista com esse nome foi encontrado"
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Nome ou CPF"
              />
            )}
            renderOption={(props, option) => {
              if (option.__separator === true) {
                const { key, ...rest } = props;
                return (
                  <Box
                    key={option.id || key}
                    component="li"
                    {...rest}
                    sx={{
                      listStyle: "none",
                      py: 0.375,
                      px: 1.25,
                      minHeight: 0,
                      cursor: "default",
                      pointerEvents: "none",
                      opacity: 1,
                      backgroundColor: "transparent",
                      "&.Mui-focused": { backgroundColor: "transparent" },
                    }}
                  >
                    <Box
                      sx={{
                        height: "1px",
                        width: "100%",
                        bgcolor: "divider",
                        opacity: 0.35,
                      }}
                    />
                  </Box>
                );
              }
              return (
                <li {...props} key={option.id || option.name || "opt"}>
                  {option.name && option.cpf
                    ? `${formatName(option.name)} - CPF: ${formatCPF(option.cpf)}`
                    : formatName(option.name)}
                </li>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              const normalizedInput = inputValue.trim();
              const normalizedCPFInput = normalizedInput.replace(/\D/g, "");

              const isNumber =
                normalizedCPFInput !== "" && !isNaN(normalizedCPFInput);

              return options.filter((option) => {
                if (option.__separator === true) {
                  return normalizedInput === "";
                }
                if (isNumber) {
                  return (option.cpf || "").includes(normalizedCPFInput);
                }
                const nameMatch = option.name
                  ?.toLowerCase()
                  .includes(normalizedInput.toLowerCase());
                return nameMatch;
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          {/* <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Dados complementares (somente leitura)
          </Typography> */}
          <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
            <Grid item xs={12} sm={2}>
              <ReadonlyField
                label="CPF"
                value={displayCpf}
                loading={loadingDetail}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ReadonlyField
                label="Placas (cavalo, carroceria e reboques)"
                value={complementary.plates}
                loading={loadingDetail}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ReadonlyField
                label="Veículo / Carroceria"
                value={complementary.vehicle}
                loading={loadingDetail}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div className="row gap-4 align-items-center gx-3">
      <div className="col-2" style={hiddenFieldStyle}>
          <label
            htmlFor="valueNegotiated"
            placeholder="0,00"
            className="font-test"
          >
            Valor Negociado:{" "}
            <span style={{ color: "red" }}>*</span>
          </label>
          <IntlCurrencyInput
            currency="BRL"
            config={currencyConfig}
            placeholder="00.00"
            onChange={onNegotiatedChange}
            value={valueNegotiated && valueNegotiated}
            className="form-control"
            id="valueNegotiated"
            disabled={verifyPayment || view}
          />

          {verifyPayment && (
            <small className="text-danger">
              O status do lançamento financeiro não permite alteração!
            </small>
          )}
        </div>
        <div className="col-2" style={hiddenFieldStyle}>
          <label
            htmlFor="paymentCondition"
            className="font-test"
            placeholder="0,00"
            style={{ whiteSpace: "nowrap" }}
          >
            Condições de Pagamento:
          </label>
          <select
            disabled={verifyPayment || view}
            value={paymentCondition && paymentCondition}
            id="paymentCondition"
            className="form-select"
            onChange={onPaymentConditionChange}
          >
            <option value="">Selecione</option>
            <option value="70/30">70%/30%</option>
            <option value="80/20">80%/20%</option>
            <option value="50/50">50%/50%</option>
            <option value="0/100">0%/100%</option>
          </select>
        </div>
        <div className="col-2" style={hiddenFieldStyle}>
          <label htmlFor="valueDriverInitial" className="font-test">
            Valor Sugerido:
          </label>
          <IntlCurrencyInput
            disabled={view}
            currency="BRL"
            config={currencyConfig}
            placeholder="00.00"
            onChange={onDriverInitialChange}
            value={valueDriverInitial && valueDriverInitial}
            className="form-control"
            id="valueDriverInitial"
            maxLength={12}
          />
        </div>

        <div className="col-2" style={hiddenFieldStyle}>
          <label
            htmlFor="valueDriverFinal"
            placeholder="0,00"
            className="font-test"
          >
            Valor Anunciado:{" "}
          </label>
          <IntlCurrencyInput
            disabled={view}
            currency="BRL"
            config={currencyConfig}
            placeholder="00.00"
            onChange={onDriverFinalChange}
            value={valueDriverFinal && valueDriverFinal}
            className="form-control"
            id="valueDriverFinal"
            maxLength={12}
          />
        </div>
        </div>
    </Card>
  );
}

export default FreightDriverNegotiationCard;
